import { z } from "zod"

import { getSimulatorType } from "@/lib/simulatorType"
import {
  isMediaEncryptionValue,
  MediaEncryption,
  SimulatorType,
} from "@/types/api/SimulatorType"
import type { FormData } from "@/types/FormData"
import {
  isSipAddress,
  isValidPhoneNumber,
  isValidSipAddress,
} from "@/utils/persona/phoneNumberUtils"

import { validateRealtimeAgent } from "./realtimeAgentValidation"
import {
  hasValidHostname,
  validateJsonObjectField,
  validateWebsocketAgent,
} from "./websocketAgentValidation"

const agentValidationSchema = z.object({
  display_name: z.string().min(1, "Display name is required"),
  model_type: z.string().min(1, "Model type is required"),
  phone_number: z.string().optional(),
  endpoint: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
  attributes: z.record(z.unknown()).optional(),
  prompt: z.string().optional(),
  workflows: z.record(z.unknown()).nullable().optional(),
  metric_ids: z.array(z.string()).optional(),
})

interface ValidateAgentFormOptions {
  persistedMediaEncryption?: unknown
}

function getMetadataRecord(
  metadata: FormData["metadata"] | undefined,
): Record<string, unknown> {
  return metadata ?? {}
}

export const validateAgentForm = (
  formData: FormData,
  options: ValidateAgentFormOptions = {},
): {
  success: boolean
  error?: string
} => {
  const baseValidation = agentValidationSchema.safeParse(formData)
  if (!baseValidation.success) {
    return {
      success: false,
      error: baseValidation.error.errors[0].message,
    }
  }

  const simulatorType = getSimulatorType(formData.model_type)
  const isVoiceAgent =
    simulatorType === SimulatorType.VOICE ||
    simulatorType === SimulatorType.OUTBOUND_VOICE

  if (isVoiceAgent && formData.phone_number) {
    if (isSipAddress(formData.phone_number)) {
      if (!isValidSipAddress(formData.phone_number)) {
        return {
          success: false,
          error: "Please enter a valid SIP address (e.g. sip:user@example.com)",
        }
      }
    } else if (!isValidPhoneNumber(formData.phone_number)) {
      return {
        success: false,
        error:
          "Please enter a valid phone number in E.164 format with country code (e.g. +12345678901)",
      }
    }
  }

  if (simulatorType === SimulatorType.VOICE && formData.metadata) {
    const metadata = getMetadataRecord(formData.metadata)
    if (formData.phone_number && isSipAddress(formData.phone_number)) {
      const sipHeadersError = validateJsonObjectField(
        metadata.custom_sip_headers,
        "Custom SIP Headers",
      )
      if (sipHeadersError) {
        return { success: false, error: sipHeadersError }
      }
    }

    const mediaEncryption = metadata.media_encryption
    const isUnchangedPersistedValue =
      mediaEncryption === options.persistedMediaEncryption
    if (
      mediaEncryption != null &&
      !isMediaEncryptionValue(mediaEncryption) &&
      !isUnchangedPersistedValue
    ) {
      return {
        success: false,
        error: "Media Encryption must be one of: disabled, SRTP, or DTLS",
      }
    }
    if (
      isMediaEncryptionValue(mediaEncryption) &&
      mediaEncryption !== MediaEncryption.DISABLED &&
      !isSipAddress(formData.phone_number)
    ) {
      return {
        success: false,
        error: "Media Encryption requires a SIP address",
      }
    }
  }

  const websocketError = validateWebsocketAgent(
    simulatorType,
    formData.metadata,
  )
  if (websocketError) {
    return { success: false, error: websocketError }
  }

  // Validate A2A Chat agent requirements
  if (simulatorType === SimulatorType.CHAT_A2A) {
    const metadata = getMetadataRecord(formData.metadata)
    const chatEndpoint =
      typeof metadata.chat_endpoint === "string"
        ? metadata.chat_endpoint.trim()
        : ""
    if (!chatEndpoint) {
      return {
        success: false,
        error:
          "A2A Endpoint is required for A2A Chat agents. Must be a valid JSON-RPC endpoint URL.",
      }
    }
    if (
      !chatEndpoint.startsWith("https://") &&
      !chatEndpoint.startsWith("http://")
    ) {
      return {
        success: false,
        error:
          "A2A Endpoint must be a valid URL starting with https:// or http://.",
      }
    }
    if (!hasValidHostname(chatEndpoint)) {
      return {
        success: false,
        error:
          "A2A Endpoint must be a valid URL starting with https:// or http://.",
      }
    }
  }

  const realtimeValidation = validateRealtimeAgent(
    simulatorType,
    formData.metadata,
  )
  if (realtimeValidation) {
    return realtimeValidation
  }

  return { success: true }
}
